import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/**
 * Customer settings (Manager-only). Today this is the home for the
 * Huri support-access controls — the customer's side of the project-
 * handoff feature:
 *
 *   - onboarding         → Huri has full access (setup phase).
 *   - handed off, no win → Huri has no access; manager can grant a
 *                          time-boxed window or reopen the project.
 *   - handed off, window → Huri has temporary access until it expires;
 *                          manager can close it early.
 *
 * Built to grow: more settings sections can mount under the same page.
 */
function SettingsPage() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmReopen, setConfirmReopen] = useState(false);

  const load = useCallback(() => {
    axios.get('/api/customer/support-access')
      .then(res => setStatus(res.data))
      .catch(() => setError('שגיאה בטעינת ההגדרות'));
  }, []);

  useEffect(load, [load]);

  const act = async (fn) => {
    setBusy(true);
    setError('');
    try {
      const res = await fn();
      setStatus(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בביצוע הפעולה');
    } finally {
      setBusy(false);
    }
  };

  const grant = (duration) =>
    act(() => axios.post('/api/customer/support-access/grant', { duration }));
  const revoke = () =>
    act(() => axios.post('/api/customer/support-access/revoke'));
  const reopen = () =>
    act(() => axios.post('/api/customer/support-access/reopen')).then(() => setConfirmReopen(false));

  if (!status) {
    return <div className="loading">{error || 'טוען...'}</div>;
  }

  const handedOff = status.handoff_state === 'handed_off';
  const window = status.active_window;
  const fmt = (iso) => iso
    ? new Date(iso).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem', dateStyle: 'medium', timeStyle: 'short' })
    : '';

  return (
    <div style={{ maxWidth: 720 }}>
      <h2 style={{ marginBottom: '0.35rem' }}>הגדרות</h2>
      <p style={{ color: '#6b7280', fontSize: '0.88rem', marginTop: 0, marginBottom: '1.25rem', lineHeight: 1.6 }}>
        כאן ניתן לשלוט מתי צוות חורי יכול להיכנס לחשבון שלכם לצורך תמיכה,
        ולעקוב אחרי יומן הגישה. הגדרות אתר נוספות (שם, לוגו, פרטי משתמשים)
        מנוהלות במסכים הייעודיים בתפריט הצד.
      </p>
      {error && <div className="login-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      <div className="info-card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ marginTop: 0 }}>גישת צוות חורי</h3>

        {!handedOff && (
          <p style={{ color: '#4b5563', lineHeight: 1.7 }}>
            הפרויקט נמצא בשלב ההקמה, ולצוות חורי יש כרגע גישה מלאה לצורך ההגדרה
            וההתקנה. בסיום ההקמה, צוות חורי ימסור לכם את הפרויקט והגישה שלהם
            תוסר אוטומטית.
          </p>
        )}

        {handedOff && window && (
          <>
            <p style={{ color: '#1a7f37', fontWeight: 600, lineHeight: 1.7 }}>
              לצוות חורי יש גישה זמנית לחשבון שלכם עד {fmt(window.ends_at)}.
            </p>
            <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
              ניתן לסגור את הגישה מיד בכל רגע.
            </p>
            <button className="btn btn-secondary" disabled={busy} onClick={revoke}>
              סגור גישה עכשיו
            </button>
          </>
        )}

        {handedOff && !window && (
          <>
            <p style={{ color: '#4b5563', lineHeight: 1.7 }}>
              הפרויקט בניהולכם הבלעדי — לצוות חורי <b>אין</b> גישה לנתונים שלכם.
              אם אתם זקוקים לתמיכה, פתחו חלון גישה זמני לפרק הזמן הרצוי:
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
              {status.duration_options.map(opt => (
                <button
                  key={opt.key}
                  className="btn btn-primary btn-sm"
                  disabled={busy}
                  onClick={() => grant(opt.key)}
                >{opt.label}</button>
              ))}
            </div>
            <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
              הגישה תיסגר אוטומטית בתום הזמן, או כשתסגרו אותה ידנית.
            </p>
          </>
        )}
      </div>

      {handedOff && (
        <div className="info-card" style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ marginTop: 0 }}>החזרת הפרויקט לחורי</h3>
          <p style={{ color: '#4b5563', lineHeight: 1.7 }}>
            החזרת הפרויקט לחורי מעניקה לצוות חורי גישה מלאה ולא-מוגבלת בזמן, עד
            שהם ימסרו לכם את הפרויקט מחדש. השתמשו בזה כשנדרשת עבודה ממושכת על
            החשבון.
          </p>
          {!confirmReopen ? (
            <button className="btn btn-secondary" disabled={busy} onClick={() => setConfirmReopen(true)}>
              החזר את הפרויקט לחורי
            </button>
          ) : (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ color: '#b91c1c', fontWeight: 600 }}>
                לאשר החזרת גישה מלאה לחורי?
              </span>
              <button className="btn btn-primary btn-sm" disabled={busy} onClick={reopen}>כן, החזר</button>
              <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setConfirmReopen(false)}>ביטול</button>
            </div>
          )}
        </div>
      )}

      <div className="info-card">
        <h3 style={{ marginTop: 0 }}>יומן גישת חורי</h3>
        {status.audit && status.audit.length > 0 ? (
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>פעולה</th>
                  <th>מבצע</th>
                  <th>מתי</th>
                </tr>
              </thead>
              <tbody>
                {status.audit.map(e => (
                  <tr key={e.id}>
                    <td>{e.action_label}{e.reason ? ` — ${e.reason}` : ''}</td>
                    <td>{e.actor_is_huri ? 'צוות חורי' : (e.actor_name || 'אתם')}</td>
                    <td>{fmt(e.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ color: '#9ca3af' }}>אין רישומים עדיין.</p>
        )}
      </div>
    </div>
  );
}

export default SettingsPage;
